import { useEffect, useState, useCallback } from "react";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Panel } from "@/components/Panel";
import { useBusquedaLive } from "@/lib/busquedaHeader";
import { Pencil, X, Plus } from "lucide-react";

const VACIO = { nombre: "", primer_apellido: "", segundo_apellido: "", person_id: "", correo: "", activo: true };

const validar = (v) => {
  const e = {};
  if (!v.nombre.trim()) e.nombre = "Ingrese nombre";
  if (!v.primer_apellido.trim()) e.primer_apellido = "Ingrese primer apellido";
  if (!v.person_id.trim()) e.person_id = "Ingrese número de ID";
  if (v.correo.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.correo.trim())) e.correo = "Correo electrónico inválido";
  return e;
};

const Campo = ({ label, obligatorio, error, testId, children }) => (
  <div className="space-y-1.5">
    <label className="text-sm font-medium text-slate-700">
      {label} {obligatorio && <span className="text-red-500">*</span>}
    </label>
    {children}
    {error && <p data-testid={`${testId}-error`} className="text-xs text-red-500">{error}</p>}
  </div>
);

export default function Empleados() {
  const [empleados, setEmpleados] = useState([]);
  const [q] = useBusquedaLive("Buscar en empleados…");
  const [form, setForm] = useState(null);
  const [errores, setErrores] = useState({});
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    const r = await api.get("/empleados", { params: q ? { q } : {} });
    setEmpleados(r.data);
  }, [q]);

  useEffect(() => { const t = setTimeout(cargar, 300); return () => clearTimeout(t); }, [cargar]);

  const eliminar = async (emp) => {
    if (!window.confirm(`¿Eliminar el empleado "${[emp.nombre, emp.primer_apellido].filter(Boolean).join(" ")}"?`)) return;
    try { await api.delete(`/empleados/${emp.id}`); toast.success("Empleado eliminado"); cargar(); }
    catch (e) { toast.error(errorMsg(e)); }
  };

  const guardar = async () => {
    const e = validar(form);
    setErrores(e);
    if (Object.keys(e).length) return;
    setGuardando(true);
    try {
      if (form.id) {
        await api.patch(`/empleados/${form.id}`, form);
        toast.success("Empleado actualizado");
      } else {
        await api.post("/empleados", form);
        toast.success("Empleado creado");
      }
      setForm(null); setErrores({});
      cargar();
    } catch (err) {
      const msg = errorMsg(err);
      if (err?.response?.status === 409) {
        setErrores(msg.toLowerCase().includes("correo") ? { correo: msg } : { person_id: msg });
      }
      toast.error(msg);
    } finally { setGuardando(false); }
  };

  const set = (campo, v) => setForm({ ...form, [campo]: v });

  return (
    <div data-testid="empleados-page" className="space-y-4">
      <Panel titulo="Listado de Empleados" testId="empleados-panel">
        <div className="flex justify-end mb-4">
          <Button data-testid="empleado-agregar-btn" onClick={() => { setForm(VACIO); setErrores({}); }}
            className="bg-indigo-600 hover:bg-indigo-700 text-white">
            <Plus size={16} className="mr-1" /> Agregar
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table data-testid="empleados-tabla" className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                <th className="py-2.5 pr-3 font-semibold">Nombre</th>
                <th className="py-2.5 pr-3 font-semibold">Primer Apellido</th>
                <th className="py-2.5 pr-3 font-semibold">Segundo Apellido</th>
                <th className="py-2.5 pr-3 font-semibold">Número de ID</th>
                <th className="py-2.5 pr-3 font-semibold">Correo Electrónico</th>
                <th className="py-2.5 pr-3 font-semibold">Estado</th>
                <th className="py-2.5 text-right font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {empleados.map((emp) => (
                <tr key={emp.id} data-testid={`empleado-row-${emp.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2.5 pr-3 font-medium text-slate-800">{emp.nombre}</td>
                  <td className="py-2.5 pr-3">{emp.primer_apellido}</td>
                  <td className="py-2.5 pr-3">{emp.segundo_apellido || "—"}</td>
                  <td className="py-2.5 pr-3">{emp.person_id}</td>
                  <td className="py-2.5 pr-3 text-slate-600">{emp.correo || "—"}</td>
                  <td className="py-2.5 pr-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      emp.activo ? "bg-teal-50 text-teal-700" : "bg-slate-100 text-slate-500"}`}>
                      {emp.activo ? "Activo" : "Inactivo"}
                    </span>
                  </td>
                  <td className="py-2.5">
                    <div className="flex items-center justify-end gap-1.5">
                      <button data-testid={`empleado-editar-btn-${emp.id}`} title="Editar"
                        onClick={() => { setForm({ ...emp, segundo_apellido: emp.segundo_apellido || "", correo: emp.correo || "" }); setErrores({}); }}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-indigo-50 text-indigo-600 hover:bg-indigo-100">
                        <Pencil size={13} />
                      </button>
                      <button data-testid={`empleado-eliminar-btn-${emp.id}`} title="Eliminar" onClick={() => eliminar(emp)}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-pink-50 text-pink-600 hover:bg-pink-100">
                        <X size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {empleados.length === 0 && (
                <tr><td colSpan={7} className="py-8 text-center text-slate-400" data-testid="empleados-vacio">
                  No se encontraron empleados
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      <Dialog open={!!form} onOpenChange={(o) => { if (!o) { setForm(null); setErrores({}); } }}>
        <DialogContent data-testid="empleado-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{form?.id ? "Editar empleado" : "Nuevo empleado"}</DialogTitle>
            <DialogDescription>Complete los datos del empleado.</DialogDescription>
          </DialogHeader>
          {form && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Campo label="Nombre" obligatorio error={errores.nombre} testId="empleado-nombre">
                  <Input data-testid="empleado-nombre-input" value={form.nombre}
                    onChange={(e) => set("nombre", e.target.value)} placeholder="Nombre" />
                </Campo>
                <Campo label="Primer Apellido" obligatorio error={errores.primer_apellido} testId="empleado-primer-apellido">
                  <Input data-testid="empleado-primer-apellido-input" value={form.primer_apellido}
                    onChange={(e) => set("primer_apellido", e.target.value)} placeholder="Primer apellido" />
                </Campo>
                <Campo label="Segundo Apellido" error={errores.segundo_apellido} testId="empleado-segundo-apellido">
                  <Input data-testid="empleado-segundo-apellido-input" value={form.segundo_apellido}
                    onChange={(e) => set("segundo_apellido", e.target.value)} placeholder="Segundo apellido" />
                </Campo>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Campo label="Número de ID" obligatorio error={errores.person_id} testId="empleado-person-id">
                  <Input data-testid="empleado-person-id-input" value={form.person_id}
                    onChange={(e) => set("person_id", e.target.value)} placeholder="Cédula / ID" />
                </Campo>
                <Campo label="Correo Electrónico" error={errores.correo} testId="empleado-correo">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium">@</span>
                    <Input data-testid="empleado-correo-input" value={form.correo} className="pl-8"
                      onChange={(e) => set("correo", e.target.value)} placeholder="correo@ejemplo.com" />
                  </div>
                </Campo>
                <Campo label="Estado" testId="empleado-activo">
                  <label className="flex items-center gap-3 h-9 text-sm text-slate-600">
                    <Switch data-testid="empleado-activo-switch" checked={form.activo}
                      onCheckedChange={(v) => set("activo", v)} />
                    {form.activo ? "Activo" : "Inactivo"}
                  </label>
                </Campo>
              </div>
              <div className="flex justify-end gap-2 mt-2">
                <Button variant="outline" data-testid="empleado-cancelar-btn"
                  className="border-teal-500 text-teal-600 hover:bg-teal-50 hover:text-teal-700"
                  onClick={() => { setForm(null); setErrores({}); }}>Cancelar</Button>
                <Button data-testid="empleado-guardar-btn" onClick={guardar} disabled={guardando}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-6">
                  {guardando ? "Guardando..." : "Guardar"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
